import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  updateDoc,
  increment
} from 'firebase/firestore';
import { handleAuthError, validateEmailDomain } from './auth-error-handlers';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from './image-compression';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};
// Initialize Firebase only once
export let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  if (!/already exists/.test(error.message)) {
    console.error('Firebase initialization error', error.stack);
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Configure Google provider
const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Initialize user data with credits
async function initializeUserData(userId, userData) {
  const userRef = doc(db, 'users', userId);

  await setDoc(userRef, {
    ...userData,
    // userTier: 'free',
    credits: 5, // Initial free credits
    premiumCredits: 0, // Purchased credits (never expire)
    lastCreditReset: serverTimestamp(),
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp()
  });
}

// Check and reset credits if needed (monthly)
export async function checkAndResetCredits(userId) {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    throw new Error('User data not found');
  }

  const userData = userDoc.data();
  const lastReset = userData.lastCreditReset?.toDate() || new Date(0);
  const now = new Date();

  // Only reset regular credits, premium credits remain unchanged
  if (
    lastReset.getMonth() !== now.getMonth() ||
    lastReset.getFullYear() !== now.getFullYear()
  ) {
    await updateDoc(userRef, {
      credits: 5, // Reset regular credits to initial amount
      lastCreditReset: serverTimestamp()
    });
    // Return total credits (regular + premium)
    return 5 + (userData.premiumCredits || 0);
  }

  // Return total available credits
  return (userData.credits || 0) + (userData.premiumCredits || 0);
}

// Check if user has sufficient credits
export async function checkCredits(userId) {
  const credits = await checkAndResetCredits(userId);
  return credits > 0;
}

export const signInWithGoogle = async toast => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    if (!user) {
      throw new Error('No user data returned from Google sign in');
    }

    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Initialize new user data
      await initializeUserData(user.uid, {
        name: user.displayName || 'Anonymous',
        email: user.email,
        photoURL: user.photoURL || null,
        provider: 'google'
      });
    } else {
      // Update last login
      await updateDoc(userRef, {
        lastLogin: serverTimestamp()
      });
    }

    return user;
  } catch (error) {
    // console.error('Google sign in error:', error);
    handleAuthError(error, toast, 'Google sign in failed');
    throw error;
  }
};

export const signUp = async (email, password, name, toast) => {
  if (!email || !password) {
    const error = new Error('Email and password are required');
    handleAuthError(error, toast, 'Sign up failed');
    throw error;
  }

  try {
    // Validate email domain before attempting signup
    validateEmailDomain(email);

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Update profile
    await updateProfile(user, {
      displayName: name
    });

    // Initialize user data
    await initializeUserData(user.uid, {
      name,
      email,
      provider: 'email'
    });

    // Send verification email
    await sendEmailVerification(user);

    return user;
  } catch (error) {
    handleAuthError(error, toast, 'Sign up failed');
    throw error;
  }
};

export const signIn = async (email, password, toast) => {
  if (!email || !password) {
    const error = new Error('Email and password are required');
    handleAuthError(error, toast, 'Sign in failed');
    throw error;
  }

  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  } catch (error) {
    handleAuthError(error, toast, 'Sign in failed');
    throw error;
  }
};

export const logOut = async toast => {
  try {
    await signOut(auth);
  } catch (error) {
    handleAuthError(error, toast, 'Sign out failed');
    throw error;
  }
};

export const onAuthStateChange = callback => onAuthStateChanged(auth, callback);

export async function uploadImage(imageFile, userId, type = 'plant') {
  try {
    // Generate a unique filename using timestamp and random string
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 8);
    const filename = `${timestamp}-${randomString}.jpg`;

    // Create a reference to the file location based on type
    const folder = type === 'disease' ? 'disease-images' : 'plant-images';
    const storageRef = ref(storage, `${folder}/${userId}/${filename}`);

    // Compress the image
    const compressedBlob = await compressImage(imageFile);

    // Upload the compressed file
    const snapshot = await uploadBytes(storageRef, compressedBlob);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
}

export async function resetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
}
